/**
 * Block "Galleria a griglia": layout justified ad ALTEZZA FISSA (stile portfolio).
 *
 * Impacchetta le righe a una stessa altezza (targetRowHeight) e scala le larghezze
 * per far combaciare ogni riga col bordo: tutte le righe restano alte uguali e le
 * foto vengono ritagliate un po' ai lati (object-cover), mantenendo grosso modo il
 * loro ratio. L'ultima riga resta a larghezza naturale, allineata a sinistra (niente
 * stiramento). Posizioniamo gli item in assoluto con la geometria calcolata.
 *
 * - Ricalcolo su resize (rAF) e al cambio stagione (body[data-season]).
 * - Stagionalità: gli item non della stagione corrente vengono esclusi dal calcolo
 *   e nascosti (la galleria si ricompone senza buchi).
 * - Reveal allo scroll via IntersectionObserver (robusto al toggle stagione).
 * - Progressive enhancement: senza JS resta il fallback flexbox (vedi app.css).
 */
export function initGalleriaGriglia() {
	const grids = [ ...document.querySelectorAll( '.tm-gallery-grid' ) ];
	if ( ! grids.length ) {
		return;
	}

	// Gutter risolto in px PRIMA di passare al layout assoluto (gap ancora attivo).
	const gutter = parseFloat( getComputedStyle( grids[ 0 ] ).columnGap ) || 16;

	const reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	document.documentElement.classList.add( 'tm-galg-ready' );

	// Observer per il reveal (condiviso tra le griglie).
	let io = null;
	if ( ! reduce && 'IntersectionObserver' in window ) {
		io = new IntersectionObserver(
			( entries ) => {
				entries.forEach( ( entry ) => {
					if ( entry.isIntersecting ) {
						entry.target.classList.add( 'is-in' );
						io.unobserve( entry.target );
					}
				} );
			},
			{ rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
		);
	}

	// Altezza-riga target: quanto sono grandi le foto (stile portfolio, imponente).
	// Più è alta, meno foto per riga → immagini più grandi. Scala per breakpoint.
	const targetRowHeight = () => {
		if ( window.matchMedia( '(min-width: 1536px)' ).matches ) {
			return 540;
		}
		if ( window.matchMedia( '(min-width: 1024px)' ).matches ) {
			return 400;
		}
		if ( window.matchMedia( '(min-width: 640px)' ).matches ) {
			return 300;
		}
		return 240;
	};

	function visibleForSeason( item ) {
		const season = document.body.dataset.season; // 'winter' | 'summer' | undefined
		const itemSeason = item.dataset.season || 'all';
		if ( 'all' === itemSeason ) {
			return true;
		}
		if ( ! season ) {
			// Editor senza data-season: mostra inverno + sempre.
			return 'winter' === itemSeason;
		}
		return itemSeason === season;
	}

	function layout( grid ) {
		// Salta se il container non è ancora dipinto (es. tab nascosta, display:none su antenato).
		if ( grid.clientWidth === 0 ) {
			return;
		}

		const items = [ ...grid.querySelectorAll( '.tm-gallery-grid__item' ) ];
		const visible = items.filter( visibleForSeason );

		items.forEach( ( it ) => {
			if ( ! visible.includes( it ) ) {
				it.style.display = 'none';
			} else {
				it.style.display = '';
			}
		} );

		if ( ! visible.length ) {
			grid.style.height = '0px';
			return;
		}

		const H = targetRowHeight();
		const W = grid.clientWidth;
		const ar = ( el ) => parseFloat( el.dataset.ar ) || 1.5;

		// Impacchetta le righe: aggiunge foto (a larghezza naturale = ar * H) finché la
		// riga raggiunge il bordo del container, poi la chiude. L'eventuale coda resta
		// come ultima riga incompleta (non riempie).
		const rows = [];
		let row = [];
		visible.forEach( ( el ) => {
			row.push( el );
			const natW =
				row.reduce( ( sum, it ) => sum + ar( it ) * H, 0 ) +
				( row.length - 1 ) * gutter;
			if ( natW >= W ) {
				rows.push( { items: row, fill: true } );
				row = [];
			}
		} );
		if ( row.length ) {
			rows.push( { items: row, fill: false } );
		}

		// Posiziona: le righe piene vengono scalate in larghezza per combaciare col
		// bordo (scale ≤ 1 → crop laterale via object-cover); tutte alte H. L'ultima
		// riga incompleta resta a larghezza naturale (scale 1), allineata a sinistra.
		let top = 0;
		rows.forEach( ( { items: rowItems, fill } ) => {
			const gutters = ( rowItems.length - 1 ) * gutter;
			const natWidths = rowItems.map( ( el ) => ar( el ) * H );
			const natSum = natWidths.reduce( ( a, b ) => a + b, 0 );
			const scale = fill ? ( W - gutters ) / natSum : 1;

			let left = 0;
			rowItems.forEach( ( el, i ) => {
				const w = natWidths[ i ] * scale;
				el.style.left = left + 'px';
				el.style.top = top + 'px';
				el.style.width = w + 'px';
				el.style.height = H + 'px';
				left += w + gutter;
			} );
			top += H + gutter;
		} );

		grid.style.height = ( top > 0 ? top - gutter : 0 ) + 'px';
	}

	const layoutAll = () => grids.forEach( layout );
	layoutAll();

	// Reveal: osserva ogni item una sola volta (anche le celle stagionali nascoste,
	// che verranno rivelate appena entrano in viewport dopo un cambio stagione).
	if ( io ) {
		grids.forEach( ( grid ) =>
			grid
				.querySelectorAll( '.tm-gallery-grid__item' )
				.forEach( ( el ) => io.observe( el ) )
		);
	}

	// Ricalcolo su resize (debounce con rAF).
	let raf = 0;
	window.addEventListener( 'resize', () => {
		if ( raf ) {
			return;
		}
		raf = window.requestAnimationFrame( () => {
			raf = 0;
			layoutAll();
		} );
	} );

	// Ricalcolo al cambio stagione (body[data-season] aggiornato da Alpine).
	const seasonObserver = new MutationObserver( layoutAll );
	seasonObserver.observe( document.body, {
		attributes: true,
		attributeFilter: [ 'data-season' ],
	} );
}
